import { useEffect, useRef, useState } from "react";
import { X, Sparkles, Send, ArrowRight, MessageSquare } from "lucide-react";
import { API_ROOT } from "@/lib/api";
import { TID } from "@/constants/testIds";

const QUICK = [
    "Quali lead devo contattare oggi?",
    "Perché stanno aumentando le disiscrizioni?",
    "Quale webinar ha generato più clienti?",
    "Come posso aumentare le demo del 20%?",
    "Quali clienti rischiano di abbandonare?",
];

export function AIChatDrawer({ open, onClose }) {
    const [messages, setMessages] = useState([]); // {role, content}
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollerRef = useRef(null);

    useEffect(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
        setInput("");
        setBusy(true);
        try {
            const token = localStorage.getItem("pv_token");
            const res = await fetch(`${API_ROOT}/ai/chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: "include",
                body: JSON.stringify({ message: msg }),
            });
            if (!res.ok || !res.body) {
                setMessages((prev) => {
                    const copy = [...prev];
                    copy[copy.length - 1] = { role: "assistant", content: "Errore comunicazione AI. Riprova." };
                    return copy;
                });
                setBusy(false);
                return;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            let sseBuffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                sseBuffer += decoder.decode(value, { stream: true });
                // Split on SSE event boundary (double newline)
                let idx;
                while ((idx = sseBuffer.indexOf("\n\n")) !== -1) {
                    const raw = sseBuffer.slice(0, idx);
                    sseBuffer = sseBuffer.slice(idx + 2);
                    const lines = raw.split("\n");
                    let dataStr = "";
                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            dataStr += line.slice(6);
                        }
                    }
                    if (!dataStr) continue;
                    if (dataStr === "[DONE]") continue;
                    let text;
                    try {
                        text = JSON.parse(dataStr);
                    } catch {
                        text = dataStr;
                    }
                    acc += text;
                    setMessages((prev) => {
                        const copy = [...prev];
                        copy[copy.length - 1] = { role: "assistant", content: acc };
                        return copy;
                    });
                }
            }
        } catch (e) {
            setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: "Errore rete. Riprova." };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-navy/30 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full sm:w-[440px] h-full flex flex-col pv-glass border-l border-border/[var(--border-opacity)] shadow-2xl pv-enter">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border/[var(--border-opacity)]">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Sparkles size={15} strokeWidth={1.5} className="text-primary" />
                        </div>
                        <div>
                            <div className="text-[13.5px] font-medium leading-tight">Prime Vision AI</div>
                            <div className="text-[10.5px] text-textsubtle">Consulente strategico · Claude Sonnet 4.5</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted">
                        <X size={15} strokeWidth={1.5} />
                    </button>
                </div>

                <div ref={scrollerRef} className="flex-1 overflow-y-auto pv-scroll px-5 py-4 space-y-4">
                    {messages.length === 0 && (
                        <div className="flex flex-col items-start gap-4">
                            <div className="pv-serif text-2xl leading-snug">
                                Ciao. <span className="text-textmuted">Cosa vuoi sapere del tuo business, oggi?</span>
                            </div>
                            <div className="w-full space-y-1.5">
                                <div className="pv-eyebrow flex items-center gap-1.5"><MessageSquare size={11} strokeWidth={1.5} />Domande veloci</div>
                                {QUICK.map((q) => (
                                    <button
                                        key={q}
                                        onClick={() => send(q)}
                                        className="w-full flex items-center justify-between gap-2 text-left text-[13px] px-3 py-2 rounded-xl hover:bg-primary/[0.06] border border-border/[var(--border-opacity)] transition-colors group"
                                    >
                                        <span>{q}</span>
                                        <ArrowRight size={13} strokeWidth={1.5} className="text-textsubtle group-hover:text-primary transition-colors" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div
                                className={`max-w-[90%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-surface2 border border-border/[var(--border-opacity)]"
                                }`}
                            >
                                {m.content || (busy ? <span className="inline-flex gap-1"><Dot /><Dot delay="120ms" /><Dot delay="240ms" /></span> : "")}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="border-t border-border/[var(--border-opacity)] p-3">
                    <div className="flex items-center gap-2 rounded-2xl border border-border/[var(--border-opacity)] bg-surface px-3 py-2">
                        <input
                            data-testid={TID.aiChatInput}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            disabled={busy}
                            placeholder="Chiedi qualcosa sui tuoi dati..."
                            className="flex-1 bg-transparent outline-none text-[13.5px] placeholder:text-textsubtle disabled:opacity-60"
                        />
                        <button
                            data-testid={TID.aiChatSend}
                            onClick={() => send()}
                            disabled={busy || !input.trim()}
                            className="h-7 w-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
                        >
                            <Send size={13} strokeWidth={1.5} />
                        </button>
                    </div>
                    <div className="text-[10px] text-textsubtle mt-2 px-1">
                        Le risposte si basano sui tuoi dati reali. Prime Vision AI può sbagliare — verifica sempre prima di agire.
                    </div>
                </div>
            </div>
        </div>
    );
}

function Dot({ delay = "0ms" }) {
    return (
        <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-textmuted"
            style={{ animation: `pv-fade-up 800ms ${delay} infinite alternate` }}
        />
    );
}

/** Lightweight markdown renderer for AI chat: handles h2/h3, bold, italic, lists and horizontal rules. */
function MdLite({ text }) {
    // split into blocks by blank line
    const blocks = text.split(/\n{2,}/);
    return (
        <div className="space-y-2.5">
            {blocks.map((block, i) => {
                if (/^---+$/.test(block.trim())) {
                    return <hr key={i} className="border-border/[var(--border-opacity)] my-1" />;
                }
                if (/^###\s+/.test(block)) {
                    return <div key={i} className="text-[13px] font-medium text-textmain">{inline(block.replace(/^###\s+/, ""))}</div>;
                }
                if (/^##\s+/.test(block)) {
                    return <div key={i} className="text-[14px] font-medium text-textmain pv-eyebrow-alt">{inline(block.replace(/^##\s+/, ""))}</div>;
                }
                if (/^\s*[-*]\s+/m.test(block)) {
                    const items = block.split(/\n/).filter((l) => /^\s*[-*]\s+/.test(l));
                    return (
                        <ul key={i} className="space-y-1 list-none pl-0">
                            {items.map((it, k) => (
                                <li key={k} className="flex items-start gap-2">
                                    <span className="mt-1.5 h-1 w-1 rounded-full bg-textsubtle flex-shrink-0" />
                                    <span>{inline(it.replace(/^\s*[-*]\s+/, ""))}</span>
                                </li>
                            ))}
                        </ul>
                    );
                }
                return <p key={i}>{inline(block)}</p>;
            })}
        </div>
    );
}

function inline(str) {
    // Convert **bold** first
    const parts = [];
    let rest = str;
    let k = 0;
    while (rest.length) {
        const m = rest.match(/\*\*(.+?)\*\*/);
        if (!m) { parts.push(rest); break; }
        if (m.index > 0) parts.push(rest.slice(0, m.index));
        parts.push(<b key={k++}>{m[1]}</b>);
        rest = rest.slice(m.index + m[0].length);
    }
    return parts;
}
